import React, { useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Breadcrumbs,
  Link,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Tabs,
  Tab,
  Avatar,
  Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import { useQueryCoordinate } from '../../../clients/graphql/graphql.hooks';
import { FETCH_METRICS_QUERY, TEAMS_GET_QUERY, TEAM_PORTFOLIOS_QUERY, TEAM_METRICS_QUERY, TEAM_PORTFOLIO_METRICS_QUERY, MetricsFetchInput, Metric, Team, TeamsGetInput, Portfolio, PortfolioMetrics, MetricsInput } from '../../../clients/graphql/pages/metrics-queries';
import { LineChart } from '@mui/x-charts/LineChart';
import GroupIcon from '@mui/icons-material/Group';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BusinessIcon from '@mui/icons-material/Business';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import BalanceIcon from '@mui/icons-material/Balance';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PeopleIcon from '@mui/icons-material/People';

// Styled component for live portfolio chips with heartbeat effect
const LiveChip = styled(Chip)(({ theme }) => ({
  animation: 'heartbeat 2s ease-in-out infinite',
  '@keyframes heartbeat': {
    '0%': {
      transform: 'scale(1)',
      boxShadow: '0 0 0 0 rgba(76, 175, 80, 0.7)',
    },
    '14%': {
      transform: 'scale(1.1)',
      boxShadow: '0 0 0 10px rgba(76, 175, 80, 0)',
    },
    '28%': {
      transform: 'scale(1)',
      boxShadow: '0 0 0 0 rgba(76, 175, 80, 0)',
    },
    '42%': {
      transform: 'scale(1.1)',
      boxShadow: '0 0 0 10px rgba(76, 175, 80, 0)',
    },
    '70%': {
      transform: 'scale(1)',
      boxShadow: '0 0 0 0 rgba(76, 175, 80, 0)',
    },
  },
}));

export const MetricsAppsPage = () => {
  const navigate = useNavigate();
  const params = useParams();
  const currentRoute = params['*'] || '';
  const [timeRange, setTimeRange] = useState<'ONE_DAY' | 'ONE_WEEK' | 'ONE_MONTH' | 'SIXTY_DAYS' | 'NINETY_DAYS' | 'ONE_YEAR' | 'THREE_YEARS' | 'FIVE_YEARS' | 'MAX'>('ONE_MONTH');
  const [metricValueType, setMetricValueType] = useState<'NEW_ACCOUNTS' | 'AUM'>('NEW_ACCOUNTS');
  
  // TODO: Add actual metrics data fetching logic here
  const loading = false;
  const error = null;
  const data = null;

  const handleCardClick = (route: string) => {
    navigate(`/developer/metrics/${route}`);
  };

  const handleBackClick = () => {
    navigate('/developer/metrics');
  };

  const handleTimeRangeChange = (
    event: React.MouseEvent<HTMLElement>,
    newTimeRange: 'ONE_DAY' | 'ONE_WEEK' | 'ONE_MONTH' | 'SIXTY_DAYS' | 'NINETY_DAYS' | 'ONE_YEAR' | 'THREE_YEARS' | 'FIVE_YEARS' | 'MAX' | null,
  ) => {
    if (newTimeRange !== null) {
      setTimeRange(newTimeRange);
    }
  };

  const handleMetricTypeChange = (event: React.SyntheticEvent, newValue: 'NEW_ACCOUNTS' | 'AUM' | null) => {
    if (newValue !== null) {
      setMetricValueType(newValue);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, px: 2 }}>
        <Alert severity="error">
          Error fetching metrics. Please reload the page.
        </Alert>
      </Box>
    );
  }

  const metricOptions = [
    {
      title: 'Teams',
      description: 'View team performance metrics and analytics',
      icon: <GroupIcon sx={{ fontSize: 40 }} />,
      route: 'teams',
      color: '#1976d2'
    },
    {
      title: 'Portfolios',
      description: 'Monitor portfolio performance and metrics',
      icon: <AccountBalanceIcon sx={{ fontSize: 40 }} />,
      route: 'portfolios',
      color: '#388e3c'
    },
    {
      title: 'Institutions',
      description: 'Track institution-level metrics and insights',
      icon: <BusinessIcon sx={{ fontSize: 40 }} />,
      route: 'institutions',
      color: '#f57c00'
    },
    {
      title: 'Balancing States',
      description: 'Monitor portfolio balancing states and lifecycle metrics',
      icon: <BalanceIcon sx={{ fontSize: 40 }} />,
      route: 'balancing',
      color: '#d32f2f'
    },
    {
      title: 'Trading Metrics',
      description: 'Real-time trading order metrics and analytics',
      icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
      route: 'trading',
      color: '#9c27b0'
    },
  ];

  // If we're on the institutions page, show the metrics
  if (currentRoute === 'institutions') {
    return <InstitutionsMetricsView 
      timeRange={timeRange} 
      onTimeRangeChange={handleTimeRangeChange}
      metricValueType={metricValueType}
      onMetricTypeChange={handleMetricTypeChange}
    />;
  }
  // If we're on the balancing states page, redirect to balancing states dashboard
  if (currentRoute === 'balancing') {
    window.open('/balancing-states-metrics', '_blank');
    // Redirect back to metrics home after opening new tab
    navigate('/developer/metrics');
    return null;
  }
  // If we're on the trading metrics page, redirect to trading metrics dashboard
  if (currentRoute === 'trading') {
    window.open('/trading-metrics', '_blank');
    // Redirect back to metrics home after opening new tab
    navigate('/developer/metrics');
    return null;
  }
  // If we're on the teams page, show the teams view
  if (currentRoute.startsWith('teams')) {
    return <TeamsMetricsView />;
  }

  // If we're on a specific metrics page, show that content
  if (currentRoute && ['portfolios'].includes(currentRoute)) {
    const currentOption = metricOptions.find(option => option.route === currentRoute);
    
    return (
      <Box sx={{ maxWidth: 1400, mx: 'auto', mt: 4, px: 2 }}>
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            component={RouterLink}
            to="/developer/metrics"
            color="inherit"
            sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
          >
            <ArrowBackIcon sx={{ mr: 1 }} />
            Metrics
          </Link>
          <Typography color="text.primary">{currentOption?.title}</Typography>
        </Breadcrumbs>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <Box sx={{ color: currentOption?.color, mr: 2 }}>
            {currentOption?.icon}
          </Box>
          <Typography variant="h4">
            {currentOption?.title} Metrics
          </Typography>
        </Box>
        
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            {currentOption?.title} metrics dashboard coming soon...
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
            This page will display {currentOption?.title.toLowerCase()} metrics and monitoring data.
          </Typography>
        </Box>
      </Box>
    );
  }

  // Main metrics page with card options
  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', mt: 4, px: 2 }}>
      <Typography variant="h4" gutterBottom textAlign="center" sx={{ mb: 4 }}>
        Metrics & Apps
      </Typography>
      
      <Grid container spacing={3} justifyContent="center">
        {metricOptions.map((option) => (
          <Grid item xs={12} sm={6} md={4} key={option.route}>
            <Card 
              sx={{ 
                height: '100%',
                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                }
              }}
            >
              <CardActionArea 
                onClick={() => handleCardClick(option.route)}
                sx={{ height: '100%', p: 3 }}
              >
                <CardContent sx={{ textAlign: 'center' }}>
                  <Box sx={{ color: option.color, mb: 2 }}>
                    {option.icon}
                  </Box>
                  <Typography variant="h6" component="div" gutterBottom>
                    {option.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {option.description}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

interface InstitutionsMetricsViewProps {
  timeRange: 'ONE_DAY' | 'ONE_WEEK' | 'ONE_MONTH' | 'SIXTY_DAYS' | 'NINETY_DAYS' | 'ONE_YEAR' | 'THREE_YEARS' | 'FIVE_YEARS' | 'MAX';
  onTimeRangeChange: (event: React.MouseEvent<HTMLElement>, newTimeRange: 'ONE_DAY' | 'ONE_WEEK' | 'ONE_MONTH' | 'SIXTY_DAYS' | 'NINETY_DAYS' | 'ONE_YEAR' | 'THREE_YEARS' | 'FIVE_YEARS' | 'MAX' | null) => void;
  metricValueType: 'NEW_ACCOUNTS' | 'AUM';
  onMetricTypeChange: (event: React.SyntheticEvent, newValue: 'NEW_ACCOUNTS' | 'AUM' | null) => void;
}

const InstitutionsMetricsView: React.FC<InstitutionsMetricsViewProps> = ({ 
  timeRange, 
  onTimeRangeChange, 
  metricValueType, 
  onMetricTypeChange 
}) => {
  const brokerages = [
    { name: 'ROBINHOOD', entityValue: 1, color: '#00C805' },
    { name: 'FIDELITY', entityValue: 4, color: '#0066CC' },
    { name: 'SCHWAB', entityValue: 6, color: '#004D99' },
    { name: 'PUBLIC', entityValue: 11, color: '#FF6B35' },
    { name: 'WEBULL', entityValue: 5, color: '#D32F2F' },
  ];

  const timeRangeLabels = {
    ONE_DAY: '1 Day',
    ONE_WEEK: '1 Week',
    ONE_MONTH: '1 Month',
    SIXTY_DAYS: '60 Days',
    NINETY_DAYS: '90 Days',
    ONE_YEAR: '1 Year',
    THREE_YEARS: '3 Years',
    FIVE_YEARS: '5 Years',
    MAX: 'Max',
  };

  const metricTypeLabels = {
    NEW_ACCOUNTS: 'New Accounts',
    AUM: 'Assets Under Management',
  };

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', mt: 4, px: 2 }}>
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component={RouterLink}
          to="/developer/metrics"
          color="inherit"
          sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
        >
          <ArrowBackIcon sx={{ mr: 1 }} />
          Metrics
        </Link>
        <Typography color="text.primary">Institutions</Typography>
      </Breadcrumbs>
      
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <Box sx={{ color: '#f57c00', mr: 2 }}>
          <BusinessIcon sx={{ fontSize: 40 }} />
        </Box>
        <Typography variant="h4">
          Institution Metrics
        </Typography>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Metric Type
        </Typography>
        <Tabs 
          value={metricValueType} 
          onChange={onMetricTypeChange}
          aria-label="metric type tabs"
          sx={{ mb: 3 }}
        >
          <Tab 
            value="NEW_ACCOUNTS" 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonAddIcon />
                New Accounts
              </Box>
            }
          />
          <Tab 
            value="AUM" 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccountBalanceWalletIcon />
                Assets Under Management
              </Box>
            }
          />
        </Tabs>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Time Range
        </Typography>
        <ToggleButtonGroup
          value={timeRange}
          exclusive
          onChange={onTimeRangeChange}
          aria-label="time range"
          size="small"
        >
          {Object.entries(timeRangeLabels).map(([key, label]) => (
            <ToggleButton key={key} value={key} aria-label={label}>
              {label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Grid container spacing={3}>
        {brokerages.map((brokerage) => (
          <Grid item xs={12} key={brokerage.entityValue}>
            <BrokerageMetricsCard 
              brokerage={brokerage} 
              timeRange={timeRange}
              metricValueType={metricValueType}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

interface BrokerageMetricsCardProps {
  brokerage: { name: string; entityValue: number; color: string };
  timeRange: 'ONE_DAY' | 'ONE_WEEK' | 'ONE_MONTH' | 'SIXTY_DAYS' | 'NINETY_DAYS' | 'ONE_YEAR' | 'THREE_YEARS' | 'FIVE_YEARS' | 'MAX';
  metricValueType: 'NEW_ACCOUNTS' | 'AUM';
}

const BrokerageMetricsCard: React.FC<BrokerageMetricsCardProps> = ({ 
  brokerage, 
  timeRange, 
  metricValueType 
}) => {
  const { loading, error, data } = useQueryCoordinate(FETCH_METRICS_QUERY, {
    variables: {
      input: {
        entityType: 'INSTITUTION',
        entityValue: brokerage.entityValue,
        metricType: 'COUNT',
        metricValueType: metricValueType,
        metricsTimeRange: timeRange,
      } as MetricsFetchInput,
    },
  });

  if (loading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {brokerage.name}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {brokerage.name}
        </Typography>
        <Alert severity="error">
          Error loading metrics for {brokerage.name}
        </Alert>
      </Paper>
    );
  }

  const metrics = data?.metricsFetch?.metrics || [];
  
  // Debug logging
  console.log('Metrics data:', { data, metrics, brokerage: brokerage.name, metricValueType, timeRange });
  
  // Sort metrics by date
  const sortedMetrics = [...metrics].sort((a, b) => 
    new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );

  const chartData = sortedMetrics.map((metric, index) => ({
    id: index,
    date: new Date(metric.createdAt).toLocaleDateString(),
    value: metric.value, // API returns actual dollar amounts, not cents
    delta: metric.delta || 0, // API returns actual dollar amounts, not cents
  }));

  // Debug chart data
  console.log('Chart data:', { chartData, sortedMetrics });

  const metricTypeLabel = metricValueType === 'NEW_ACCOUNTS' ? 'New Accounts' : 'Assets Under Management';
  const valueFormatter = metricValueType === 'AUM' ? (value: number) => `$${value.toLocaleString()}` : (value: number) => value.toString();
  const tooltipFormatter = metricValueType === 'AUM' 
    ? (value: number | null) => value !== null ? `$${value.toLocaleString()}` : 'N/A'
    : (value: number | null) => value !== null ? value.toString() : 'N/A';

  // Show message if no data
  if (metrics.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ color: brokerage.color }}>
          {brokerage.name} - {metricTypeLabel}
        </Typography>
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No metrics data available for {brokerage.name} in the selected time range.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Try selecting a different time range or metric type.
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ color: brokerage.color }}>
        {brokerage.name} - {metricTypeLabel}
      </Typography>
      
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" gutterBottom>
            Value Over Time
          </Typography>
          <Box sx={{ height: 300, width: '100%' }}>
            <LineChart
              dataset={chartData}
              xAxis={[{
                dataKey: 'date',
                scaleType: 'band',
                valueFormatter: (value) => value,
              }]}
              yAxis={[{
                valueFormatter: valueFormatter,
              }]}
              series={[
                {
                  dataKey: 'value',
                  label: 'Value',
                  color: brokerage.color,
                  valueFormatter: tooltipFormatter,
                },
              ]}
              height={300}
              width={500}
              margin={{ left: 100, right: 20, top: 20, bottom: 40 }}
            />
          </Box>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" gutterBottom>
            Delta Over Time
          </Typography>
          <Box sx={{ height: 300, width: '100%' }}>
            <LineChart
              dataset={chartData}
              xAxis={[{
                dataKey: 'date',
                scaleType: 'band',
                valueFormatter: (value) => value,
              }]}
              yAxis={[{
                valueFormatter: valueFormatter,
              }]}
              series={[
                {
                  dataKey: 'delta',
                  label: 'Delta',
                  color: brokerage.color,
                  valueFormatter: tooltipFormatter,
                },
              ]}
              height={300}
              width={500}
              margin={{ left: 100, right: 20, top: 20, bottom: 40 }}
            />
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
}; 

const TeamsMetricsView: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const currentRoute = params['*'] || '';
  
  // Extract teamKey from the route (e.g., "teams/123" -> teamKey = 123)
  const teamKeyMatch = currentRoute.match(/^teams\/(\d+)$/);
  const teamKey = teamKeyMatch ? parseInt(teamKeyMatch[1]) : null;
  
  // If we have a teamKey, show team details
  if (teamKey) {
    return <TeamDetailView teamKey={teamKey} />;
  }
  
  // Otherwise show the teams list
  return <TeamsListView />;
};

const TeamsListView: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [limit] = useState(20);
  
  const { loading, error, data } = useQueryCoordinate(TEAMS_GET_QUERY, {
    variables: {
      input: {
        limit: limit,
        offset: page * limit,
      } as TeamsGetInput,
    },
  });

  const handleTeamClick = (teamKey: number) => {
    navigate(`/developer/metrics/teams/${teamKey}`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, px: 2 }}>
        <Alert severity="error">
          Error fetching teams. Please reload the page.
        </Alert>
      </Box>
    );
  }

  const teams = data?.teamsGet?.teams || [];

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', mt: 4, px: 2 }}>
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component={RouterLink}
          to="/developer/metrics"
          color="inherit"
          sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
        >
          <ArrowBackIcon sx={{ mr: 1 }} />
          Metrics
        </Link>
        <Typography color="text.primary">Teams</Typography>
      </Breadcrumbs>
      
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <Box sx={{ color: '#1976d2', mr: 2 }}>
          <GroupIcon sx={{ fontSize: 40 }} />
        </Box>
        <Typography variant="h4">
          Teams Metrics
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {teams.map((team: Team) => (
          <Grid item xs={12} sm={6} md={4} key={team.teamKey}>
            <Card 
              sx={{ 
                height: '100%',
                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                }
              }}
            >
              <CardActionArea 
                onClick={() => handleTeamClick(team.teamKey)}
                sx={{ height: '100%', p: 3 }}
              >
                <CardContent sx={{ textAlign: 'center' }}>
                  <Box sx={{ mb: 2 }}>
                    {team.companyImageUrl ? (
                      <Avatar
                        src={team.companyImageUrl}
                        sx={{ width: 80, height: 80, mx: 'auto', mb: 2 }}
                      />
                    ) : (
                      <Avatar sx={{ width: 80, height: 80, mx: 'auto', mb: 2, bgcolor: '#1976d2' }}>
                        <PeopleIcon sx={{ fontSize: 40 }} />
                      </Avatar>
                    )}
                  </Box>
                  <Typography variant="h6" component="div" gutterBottom>
                    {team.title || `Team ${team.teamKey}`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {team.description}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
                    {team.isLive && (
                      <Chip 
                        label="Live" 
                        color="success" 
                        size="small" 
                        variant="outlined"
                      />
                    )}
                    {team.isIRA && (
                      <Chip 
                        label="IRA" 
                        color="primary" 
                        size="small" 
                        variant="outlined"
                      />
                    )}
                    {team.isPlatformTeam && (
                      <Chip 
                        label="Platform" 
                        color="secondary" 
                        size="small" 
                        variant="outlined"
                      />
                    )}
                  </Box>
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem', color: 'text.secondary' }}>
                    <span>Portfolios: {team.portfolioCount || 0}</span>
                    <span>Followers: {team.followerCount || 0}</span>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}; 

interface TeamDetailViewProps {
  teamKey: number;
}

const TeamDetailView: React.FC<TeamDetailViewProps> = ({ teamKey }) => {
  const navigate = useNavigate();
  
  const { loading, error, data } = useQueryCoordinate(TEAMS_GET_QUERY, {
    variables: {
      input: {
        teamKeys: [teamKey],
        limit: 1,
        offset: 0,
      } as TeamsGetInput,
    },
  });

  const { loading: portfoliosLoading, error: portfoliosError, data: portfoliosData } = useQueryCoordinate(TEAM_PORTFOLIOS_QUERY, {
    variables: {
      input: {
        teamKeys: [teamKey],
        limit: 50,
        offset: 0,
      } as TeamsGetInput,
    },
  });

  const { loading: metricsLoading, error: metricsError, data: metricsData } = useQueryCoordinate(TEAM_METRICS_QUERY, {
    variables: {
      input: {
        teamKeys: [teamKey],
        limit: 1,
        offset: 0,
      } as TeamsGetInput,
      metricsInput: {
        metricsTimeRange: 'ONE_YEAR',
        metricsTimeMeasurementInterval: 'ONE_DAY',
      } as MetricsInput,
    },
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, px: 2 }}>
        <Alert severity="error">
          Error fetching team details. Please reload the page.
        </Alert>
      </Box>
    );
  }

  const team = data?.teamsGet?.teams?.[0];
  if (!team) {
    return (
      <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, px: 2 }}>
        <Alert severity="error">
          Team not found.
        </Alert>
      </Box>
    );
  }

  // Extract portfolios from the portfolios query data
  const portfolios = portfoliosData?.teamsGet?.teams?.[0]?.portfolios || [];

  // Extract metrics from the metrics query data
  const teamMetrics = metricsData?.teamsGet?.teams?.[0]?.metrics;

  // Sort portfolios: live first, then non-live
  const sortedPortfolios = [...portfolios].sort((a, b) => {
    const aIsLive = a.autoPilotMasterPortfolio?.isLive || false;
    const bIsLive = b.autoPilotMasterPortfolio?.isLive || false;
    return bIsLive - aIsLive; // true (1) comes before false (0)
  });

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', mt: 4, px: 2 }}>
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component={RouterLink}
          to="/developer/metrics"
          color="inherit"
          sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
        >
          <ArrowBackIcon sx={{ mr: 1 }} />
          Metrics
        </Link>
        <Link
          component={RouterLink}
          to="/developer/metrics/teams"
          color="inherit"
          sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
        >
          Teams
        </Link>
        <Typography color="text.primary">{team.title || `Team ${team.teamKey}`}</Typography>
      </Breadcrumbs>
      
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <Box sx={{ mr: 2 }}>
          {team.companyImageUrl ? (
            <Avatar
              src={team.companyImageUrl}
              sx={{ width: 60, height: 60 }}
            />
          ) : (
            <Avatar sx={{ width: 60, height: 60, bgcolor: '#1976d2' }}>
              <PeopleIcon sx={{ fontSize: 30 }} />
            </Avatar>
          )}
        </Box>
        <Box>
          <Typography variant="h4">
            {team.title || `Team ${team.teamKey}`}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {team.description}
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Team Information
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Team Key</Typography>
                <Typography variant="body1">{team.teamKey}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Created</Typography>
                <Typography variant="body1">{new Date(team.createdAt).toLocaleDateString()}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {team.isLive && <Chip label="Live" color="success" size="small" />}
                  {team.isIRA && <Chip label="IRA" color="primary" size="small" />}
                  {team.isPlatformTeam && <Chip label="Platform" color="secondary" size="small" />}
                  {team.isSubscribed && <Chip label="Subscribed" color="info" size="small" />}
                </Box>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Portfolios</Typography>
                <Typography variant="body1">{team.portfolioCount || 0}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Followers</Typography>
                <Typography variant="body1">{team.followerCount || 0}</Typography>
              </Box>
              {team.companyUrl && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Company URL</Typography>
                  <Link href={team.companyUrl} target="_blank" rel="noopener noreferrer">
                    {team.companyUrl}
                  </Link>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Quick Stats
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Live Portfolios</Typography>
                <Typography variant="body1">
                  {sortedPortfolios.filter(p => p.autoPilotMasterPortfolio?.isLive).length}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Total Portfolios</Typography>
                <Typography variant="body1">{portfolios.length}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Archived Portfolios</Typography>
                <Typography variant="body1">
                  {sortedPortfolios.filter(p => p.autoPilotMasterPortfolio?.archivedAt).length}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Team Portfolios ({portfolios.length})
            </Typography>
            {portfoliosLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : portfoliosError ? (
              <Alert severity="error">
                Error loading portfolios for this team
              </Alert>
            ) : portfolios.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  No portfolios found for this team.
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {sortedPortfolios.map((portfolio: Portfolio) => (
                  <Grid item xs={12} sm={6} md={4} key={portfolio.portfolioKey}>
                    <Card sx={{ height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          {portfolio.autoPilotMasterPortfolio?.profileImageUrl ? (
                            <Avatar
                              src={portfolio.autoPilotMasterPortfolio.profileImageUrl}
                              sx={{ width: 32, height: 32, mr: 1 }}
                            />
                          ) : (
                            <AccountBalanceIcon 
                              sx={{ 
                                mr: 1, 
                                color: portfolio.autoPilotMasterPortfolio?.isLive ? '#388e3c' : '#d32f2f' 
                              }} 
                            />
                          )}
                          <Typography variant="h6" component="div" sx={{ flex: 1 }}>
                            {portfolio.autoPilotMasterPortfolio?.title || `Portfolio ${portfolio.portfolioKey}`}
                          </Typography>
                          {portfolio.autoPilotMasterPortfolio?.isLive ? (
                            <LiveChip 
                              label="Live" 
                              color="success" 
                              size="small" 
                              variant="outlined"
                            />
                          ) : portfolio.autoPilotMasterPortfolio?.archivedAt ? (
                            <Chip 
                              label="Archived" 
                              color="default" 
                              size="small" 
                              variant="outlined"
                            />
                          ) : (
                            <Chip 
                              label="Inactive" 
                              color="error" 
                              size="small" 
                              variant="outlined"
                            />
                          )}
                        </Box>
                        {portfolio.autoPilotMasterPortfolio?.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {portfolio.autoPilotMasterPortfolio.description}
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Box>
                            <Typography variant="subtitle2" color="text.secondary">Portfolio Key</Typography>
                            <Typography variant="body1">
                              {portfolio.portfolioKey}
                            </Typography>
                          </Box>
                          {portfolio.autoPilotMasterPortfolio && (
                            <Box>
                              <Typography variant="subtitle2" color="text.secondary">Master Portfolio Key</Typography>
                              <Typography variant="body1">
                                {portfolio.autoPilotMasterPortfolio.autoPilotMasterPortfolioKey}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        {portfolio.autoPilotMasterPortfolio?.archivedAt && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                              Archived: {new Date(portfolio.autoPilotMasterPortfolio.archivedAt).toLocaleDateString()}
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Team Metrics
            </Typography>
            {metricsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : metricsError ? (
              <Alert severity="error">
                Error loading team metrics
              </Alert>
            ) : !teamMetrics ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  No metrics data available for this team.
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={3}>
                {teamMetrics.histogram && (
                  <>
                    {teamMetrics.histogram.aum && teamMetrics.histogram.aum.length > 0 && (
                      <>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle1" gutterBottom>
                            AUM Value Over Time
                          </Typography>
                          <Box sx={{ height: 300, width: '100%' }}>
                            <LineChart
                              dataset={teamMetrics.histogram.aum.map((metric: Metric, index: number) => ({
                                id: index,
                                date: new Date(metric.createdAt).toLocaleDateString(),
                                value: metric.value,
                              }))}
                              xAxis={[{
                                dataKey: 'date',
                                scaleType: 'band',
                              }]}
                              yAxis={[{
                                valueFormatter: (value) => `$${value.toLocaleString()}`,
                              }]}
                              series={[
                                {
                                  dataKey: 'value',
                                  label: 'AUM Value',
                                  color: '#1976d2',
                                  valueFormatter: (value) => `$${value?.toLocaleString() || 'N/A'}`,
                                },
                              ]}
                              height={300}
                              width={500}
                              margin={{ left: 100, right: 20, top: 20, bottom: 40 }}
                            />
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle1" gutterBottom>
                            AUM Delta Over Time
                          </Typography>
                          <Box sx={{ height: 300, width: '100%' }}>
                            <LineChart
                              dataset={teamMetrics.histogram.aum.map((metric: Metric, index: number) => ({
                                id: index,
                                date: new Date(metric.createdAt).toLocaleDateString(),
                                delta: metric.delta || 0,
                              }))}
                              xAxis={[{
                                dataKey: 'date',
                                scaleType: 'band',
                              }]}
                              yAxis={[{
                                valueFormatter: (value) => `$${value.toLocaleString()}`,
                              }]}
                              series={[
                                {
                                  dataKey: 'delta',
                                  label: 'AUM Delta',
                                  color: '#388e3c',
                                  valueFormatter: (value) => `$${value?.toLocaleString() || 'N/A'}`,
                                },
                              ]}
                              height={300}
                              width={500}
                              margin={{ left: 100, right: 20, top: 20, bottom: 40 }}
                            />
                          </Box>
                        </Grid>
                      </>
                    )}
                    {teamMetrics.histogram.subscriptions && teamMetrics.histogram.subscriptions.length > 0 && (
                      <>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle1" gutterBottom>
                            Subscriptions Value Over Time
                          </Typography>
                          <Box sx={{ height: 300, width: '100%' }}>
                            <LineChart
                              dataset={teamMetrics.histogram.subscriptions.map((metric: Metric, index: number) => ({
                                id: index,
                                date: new Date(metric.createdAt).toLocaleDateString(),
                                value: metric.value,
                              }))}
                              xAxis={[{
                                dataKey: 'date',
                                scaleType: 'band',
                              }]}
                              yAxis={[{
                                valueFormatter: (value) => value.toLocaleString(),
                              }]}
                              series={[
                                {
                                  dataKey: 'value',
                                  label: 'Subscriptions Value',
                                  color: '#f57c00',
                                  valueFormatter: (value) => value?.toLocaleString() || 'N/A',
                                },
                              ]}
                              height={300}
                              width={500}
                              margin={{ left: 100, right: 20, top: 20, bottom: 40 }}
                            />
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle1" gutterBottom>
                            Subscriptions Delta Over Time
                          </Typography>
                          <Box sx={{ height: 300, width: '100%' }}>
                            <LineChart
                              dataset={teamMetrics.histogram.subscriptions.map((metric: Metric, index: number) => ({
                                id: index,
                                date: new Date(metric.createdAt).toLocaleDateString(),
                                delta: metric.delta || 0,
                              }))}
                              xAxis={[{
                                dataKey: 'date',
                                scaleType: 'band',
                              }]}
                              yAxis={[{
                                valueFormatter: (value) => value.toLocaleString(),
                              }]}
                              series={[
                                {
                                  dataKey: 'delta',
                                  label: 'Subscriptions Delta',
                                  color: '#9c27b0',
                                  valueFormatter: (value) => value?.toLocaleString() || 'N/A',
                                },
                              ]}
                              height={300}
                              width={500}
                              margin={{ left: 100, right: 20, top: 20, bottom: 40 }}
                            />
                          </Box>
                        </Grid>
                      </>
                    )}
                  </>
                )}
              </Grid>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <PortfolioConversionChart teamKey={teamKey} />
        </Grid>
      </Grid>
    </Box>
  );
}; 

interface PortfolioConversionChartProps {
  teamKey: number;
}

const PortfolioConversionChart: React.FC<PortfolioConversionChartProps> = ({ teamKey }) => {
  const { loading, error, data } = useQueryCoordinate(TEAM_PORTFOLIO_METRICS_QUERY, {
    variables: {
      input: {
        teamKeys: [teamKey],
        limit: 1,
        offset: 0,
      } as TeamsGetInput,
    },
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Error loading portfolio conversion metrics
      </Alert>
    );
  }

  const portfolios = data?.teamsGet?.teams?.[0]?.portfolios || [];
  
  // Filter portfolios that have conversion data
  const portfoliosWithData = portfolios.filter((portfolio: PortfolioMetrics) => 
    portfolio.metrics?.count?.portfolioConversion && 
    portfolio.metrics.count.portfolioConversion.length > 0
  );

  if (portfoliosWithData.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No portfolio conversion data available.
        </Typography>
      </Box>
    );
  }

  // Create a combined dataset for the chart
  // We need to get all unique dates first
  const allDates = new Set<string>();
  portfoliosWithData.forEach((portfolio: PortfolioMetrics) => {
    portfolio.metrics?.count?.portfolioConversion?.forEach((conversion) => {
      allDates.add(new Date(conversion.createdAt).toLocaleDateString());
    });
  });

  const sortedDates = Array.from(allDates).sort((a, b) => 
    new Date(a).getTime() - new Date(b).getTime()
  );

  // Create the chart dataset
  const chartData = sortedDates.map((date, index) => {
    const dataPoint: any = { id: index, date };
    
    portfoliosWithData.forEach((portfolio: PortfolioMetrics) => {
      const portfolioTitle = portfolio.autoPilotMasterPortfolio?.title || `Portfolio ${portfolio.portfolioKey}`;
      const conversionData = portfolio.metrics?.count?.portfolioConversion?.find(
        (conv) => new Date(conv.createdAt).toLocaleDateString() === date
      );
      
      if (conversionData) {
        dataPoint[`${portfolioTitle}_value`] = conversionData.value;
        dataPoint[`${portfolioTitle}_delta`] = conversionData.delta || 0;
      } else {
        dataPoint[`${portfolioTitle}_value`] = 0;
        dataPoint[`${portfolioTitle}_delta`] = 0;
      }
    });
    
    return dataPoint;
  });

  // Generate colors for each portfolio
  const colors = [
    '#1976d2', '#388e3c', '#f57c00', '#9c27b0', '#d32f2f',
    '#7b1fa2', '#c2185b', '#ff5722', '#795548', '#607d8b',
    '#3f51b5', '#009688', '#ff9800', '#e91e63', '#673ab7'
  ];

  // Create series for the chart
  const series = portfoliosWithData.map((portfolio: PortfolioMetrics, index: number) => {
    const portfolioTitle = portfolio.autoPilotMasterPortfolio?.title || `Portfolio ${portfolio.portfolioKey}`;
    return {
      dataKey: `${portfolioTitle}_value`,
      label: portfolioTitle,
      color: colors[index % colors.length],
      valueFormatter: (value: number) => value?.toLocaleString() || 'N/A',
    };
  });

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Portfolio Conversion Comparison
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Comparing conversion rates across all portfolios over time
      </Typography>
      
      <Box sx={{ height: 400, width: '100%' }}>
        <LineChart
          dataset={chartData}
          xAxis={[{
            dataKey: 'date',
            scaleType: 'band',
          }]}
          yAxis={[{
            valueFormatter: (value) => value.toLocaleString(),
          }]}
          series={series}
          height={400}
          width={800}
          margin={{ left: 100, right: 20, top: 20, bottom: 40 }}
        />
      </Box>
      
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Portfolio Legend
        </Typography>
        <Grid container spacing={1}>
          {portfoliosWithData.map((portfolio: PortfolioMetrics, index: number) => {
            const portfolioTitle = portfolio.autoPilotMasterPortfolio?.title || `Portfolio ${portfolio.portfolioKey}`;
            const totalConversions = portfolio.metrics?.count?.portfolioConversion?.reduce(
              (sum, conv) => sum + conv.value, 0
            ) || 0;
            
            return (
              <Grid item xs={12} sm={6} md={4} key={portfolio.portfolioKey}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box 
                    sx={{ 
                      width: 12, 
                      height: 12, 
                      backgroundColor: colors[index % colors.length],
                      borderRadius: '50%' 
                    }} 
                  />
                  <Typography variant="body2">
                    {portfolioTitle} ({totalConversions.toLocaleString()})
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </Paper>
  );
}; 